import { useQueries } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import {
	getDeviceEvents,
	getDeviceGeofences,
	getDeviceGeofencesReport,
	getDeviceMapLocations,
	getDeviceStopReport,
	getDeviceTimeSheets,
	getDeviceTimeSheetsReport,
	getDeviceTracks,
	getDeviceTripReport,
	getDispatchEvents,
	getFormMapEvents,
	getFormMapLocations,
	getTeamDevices,
	getTeamDeviceStopReport,
	getTeamDeviceTimeSheetsReport,
	getTeamDeviceTripReport,
} from '../mapService'
import { useEffect, useMemo } from 'react'

export const fetchers = {
	dispatch_detail: async ({
		module,
		deviceSelected,
		formSelected,
		dateValue,
		tab,
		environment,
	}) => {
		const { apiToken, projectKey } = environment
		if (tab === 'devices') {
			return { data: await getDeviceEvents(deviceSelected.key, dateValue, 0, apiToken) }
		}
		if (module.dispatchRecord4Map != null) {
			return { data: await getDispatchEvents(module.dispatchRecord4Map, apiToken) }
		}
		return {
			data: await getFormMapEvents(projectKey, formSelected.formKey, dateValue, 0, apiToken),
		}
	},

	form_detail: async ({ module, deviceSelected, formSelected, dateValue, tab, environment }) => {
		const { apiToken, projectKey } = environment
		if (tab === 'devices') {
			return { data: await getDeviceMapLocations(deviceSelected.key, dateValue, 0, apiToken) }
		}
		if (module.dispatchRecord4Map != null) {
			return { data: [] }
		}
		return {
			data: await getFormMapLocations(projectKey, formSelected.formKey, dateValue, 0, apiToken),
		}
	},

	devices_detail: async ({ deviceSelected, dateValue, environment }) => ({
		data: await getTeamDevices(deviceSelected.teamKey, dateValue, 0, environment.apiToken),
	}),

	tracking_detail: async ({ deviceSelected, dateValue, environment }) => ({
		data: await getDeviceTracks(deviceSelected.key, dateValue, 0, environment.apiToken),
	}),

	trip_report: async ({ deviceSelected, dateValue, environment }) => ({
		data:
			deviceSelected && !deviceSelected.key
				? await getTeamDeviceTripReport(deviceSelected.teamKey, dateValue, 0, environment.apiToken)
				: await getDeviceTripReport(deviceSelected.key, dateValue, 0, environment.apiToken),
	}),

	stop_report: async ({ deviceSelected, dateValue, environment }) => ({
		data:
			deviceSelected && !deviceSelected.key
				? await getTeamDeviceStopReport(deviceSelected.teamKey, dateValue, 0, environment.apiToken)
				: await getDeviceStopReport(deviceSelected.key, dateValue, 0, environment.apiToken),
	}),

	geofence_detail: async ({ deviceSelected, dateValue, environment }) => ({
		data: await getDeviceGeofences(deviceSelected.key, dateValue, 0, environment.apiToken),
	}),

	geofence_report: async ({ deviceSelected, dateValue, environment }) => ({
		data: await getDeviceGeofencesReport(deviceSelected.key, dateValue, 0, environment.apiToken),
	}),

	timesheet_detail: async ({ deviceSelected, dateValue, environment }) => ({
		data: await getDeviceTimeSheets(deviceSelected.key, dateValue, 0, environment.apiToken),
	}),

	timesheet_report: async ({ deviceSelected, dateValue, environment }) => ({
		data:
			deviceSelected && !deviceSelected.key
				? await getTeamDeviceTimeSheetsReport(
						deviceSelected.teamKey,
						dateValue,
						0,
						environment.apiToken
				  )
				: await getDeviceTimeSheetsReport(deviceSelected.key, dateValue, 0, environment.apiToken),
	}),
}
const DEFAULT_INTERVAL_VALUE = 60000
export function useMapRecordQuery({
	module,
	tab,
	formSelected,
	deviceSelected,
	detailState,
	reportState,
	dateValue,
	selectedOption,
	interval = DEFAULT_INTERVAL_VALUE,
}) {
	const { environment } = useSelector((state) => state)

	const sourceState = useMemo(() => {
		if (selectedOption === 'detail') return detailState
		if (selectedOption === 'report') return reportState
		return []
	}, [selectedOption, detailState, reportState])

	const checkedSourceState = useMemo(
		() => sourceState.filter((s) => s.checked).map((s) => s.name),
		[sourceState]
	)

	const queriesConfig = useMemo(() => {
		return sourceState
			.filter((s) => s.checked && (selectedOption === 'detail' || tab === 'devices'))
			.map((s) => {
				const type = `${s.name}_${selectedOption}`

				const queryKey = [
					'mapRecords',
					type,
					checkedSourceState,
					module?.dispatchRecord4Map || null,
					deviceSelected?.key || deviceSelected?.teamKey || null,
					formSelected?.formKey || null,
					dateValue,
					tab,
					environment?.apiToken || null,
				]

				const queryFn = () =>
					fetchers[type]({
						module,
						deviceSelected,
						formSelected,
						dateValue,
						tab,
						environment,
					})

				return {
					queryKey,
					queryFn,
					enabled: Boolean(
						environment?.apiToken &&
							(deviceSelected?.key || deviceSelected?.teamKey || formSelected?.formKey)
					),
					refetchInterval: interval,
				}
			})
	}, [
		sourceState,
		selectedOption,
		tab,
		module,
		deviceSelected,
		formSelected,
		dateValue,
		environment,
		interval,
	])

	const queries = useQueries({ queries: queriesConfig })

	const refetchData = () => {
		queries.forEach((q) => {
			if (q.refetch) q.refetch()
		})
	}
	const isLoading = queries.length > 0 && queries.every((q) => q.isLoading || q.isInitialLoading)
	const isFetching = queries.some((q) => q.isFetching)
	const allResults = queries
		.filter((q) => q.isSuccess && q.data)
		.map((q) => ({
			type: q.data.type,
			data: q.data.data.data || q.data.data || [],
		}))

	const allMapRecords = allResults.flatMap((r) => r.data)
	return { allMapRecords, isLoading, isFetching, refetchData }
}
